import React, { useState, useEffect, useCallback, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    setPersistence,
    browserLocalPersistence
} from 'firebase/auth';
import {
    getFirestore,
    doc,
    setDoc,
    getDoc,
    collection,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    addDoc,
    orderBy,
    writeBatch,
    onSnapshot,
    deleteField,
    arrayUnion,
    arrayRemove
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
// --- IMPORTANT: Firebase Configuration ---
// This configuration is being used as provided in the original file.
const firebaseConfig = {
  apiKey: "AIzaSyDbwFfyyRLPbb0B_4GUtpWeWVH3P_muL3s",
  authDomain: "project-9164549534464467337.firebaseapp.com",
  projectId: "project-9164549534464467337",
  storageBucket: "project-9164549534464467337.firebasestorage.app",
  messagingSenderId: "427075742551",
  appId: "1:427075742551:web:4c3e5a3edc5eeb8f052c12",
  measurementId: "G-W8C6EN8NNL"
};

// --- Initialize Firebase ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app, 'us-central1'); // <-- ADD THIS

// --- Main App Component ---
export default function App() {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Set the favicon dynamically
        const faviconUrl = "https://firebasestorage.googleapis.com/v0/b/project-9164549534464467337.appspot.com/o/public%2FInstill%20logo%20copy.png?alt=media&token=89364d14-d6bc-4175-a4f1-34973fd48819";
        let link = document.querySelector("link[rel*='icon']");
        if (!link) {
            link = document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = faviconUrl;


        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setLoading(true);
            if (currentUser) {
                setUser(currentUser);
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    setUserData({ uid: currentUser.uid, ...userDocSnap.data() });
                } else {
                    // This case might happen if the user record is deleted from Firestore but not from Auth
                    setUserData(null);
                }
            } else {
                setUser(null);
                setUserData(null);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Error signing out: ", error);
        }
    };

    if (loading) {
        return <LoadingScreen />;
    }

    if (!user || !userData) {
        return <AuthScreen />;
    }

    return (
        <div className="min-h-screen bg-pink-50 font-sans">
            <Header user={userData} onLogout={handleLogout} />
            <main className="p-4 md:p-8">
                {userData.role === 'user' && <UserConsole userData={userData} />}
                {userData.role === 'admin' && <AdminConsole userData={userData} />}
                {userData.role === 'superadmin' && <SuperAdminConsole userData={userData} />}
            </main>
        </div>
    );
}

// --- Loading Screen ---
const LoadingScreen = () => (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-700 via-pink-600 to-orange-500 text-white">
        <div className="text-center">
            <svg className="animate-spin h-10 w-10 text-white mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <h1 className="text-2xl font-semibold">Loading ACCA Tracker...</h1>
            <p className="text-gray-200">Please wait a moment.</p>
        </div>
    </div>
);


// --- Authentication Screen ---
const AuthScreen = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleAuthAction = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setMessage('');
        if (isLogin) {
            try {
                // Explicitly set persistence to local storage.
                // This ensures the user stays logged in across browser sessions.
                await setPersistence(auth, browserLocalPersistence);
                await signInWithEmailAndPassword(auth, email, password);
            } catch (err) {
                setError(err.message);
            }
        } else {
            // Registration is disabled from the UI for regular users.
            setError("User registration is not available. Please contact an administrator.");
        }
        setIsLoading(false);
    };

    const handlePasswordReset = async () => {
        if (!email) {
            setError("Please enter your email address to reset your password.");
            return;
        }
        setIsLoading(true);
        setError('');
        setMessage('');
        try {
            await sendPasswordResetEmail(auth, email);
            setMessage("Password reset email sent! Please check your inbox.");
        } catch (err) {
            setError(err.message);
        }
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-700 via-pink-600 to-orange-500 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-8 space-y-6">
                <div className="text-center">
                    <img
                        src="https://firebasestorage.googleapis.com/v0/b/project-9164549534464467337.firebasestorage.app/o/public%2FInstill%20logo%20copy.png?alt=media&token=89364d14-d6bc-4175-a4f1-34973fd48819"
                        alt="Instil Learning Hub Logo"
                        className="mx-auto h-24 w-auto mb-4"
                    />
                    <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-orange-500">Instil Learning Hub</h1>
                    <p className="text-gray-500 mt-2">{isLogin ? 'Sign in to your account' : 'Create a new account'}</p>
                </div>

                {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg" role="alert">{error}</div>}
                {message && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg" role="alert">{message}</div>}

                <form onSubmit={handleAuthAction} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email Address</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="mt-1 block w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            placeholder="you@example.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="mt-1 block w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            placeholder="••••••••"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:bg-pink-300"
                    >
                        {isLoading ? 'Processing...' : (isLogin ? 'Sign In' : 'Sign Up')}
                    </button>
                </form>
                <div className="text-center text-sm">
                    <button onClick={handlePasswordReset} className="font-medium text-pink-600 hover:text-pink-500">
                        Forgot your password?
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Header Component ---
const Header = ({ user, onLogout }) => (
    <>
        <div className="bg-gradient-to-r from-purple-700 via-pink-600 to-orange-500 p-4 text-white shadow-lg flex items-center justify-center space-x-4">
            <img
                src="https://firebasestorage.googleapis.com/v0/b/project-9164549534464467337.firebasestorage.app/o/public%2Flogo%20new.png?alt=media&token=5aa1e9f5-58a2-4bd9-b328-af1ca0e8dc84"
                alt="Instil Learning Hub Logo"
                className="h-20 w-auto"
            />
            <h1 className="text-3xl font-bold tracking-wider">Instil Learning Hub</h1>
        </div>
        <header className="bg-white shadow-md">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <div className="flex items-center space-x-4">
                        <svg className="h-8 w-8 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h1 className="text-xl font-bold text-gray-800">ACCA Study Tracker</h1>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="text-right">
                            <p className="text-sm font-medium text-gray-800">{user.name}</p>
                            <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                        </div>
                        <button
                            onClick={onLogout}
                            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </header>
    </>
);


// --- Study Timer Component (Now Presentational) ---
const StudyTimer = ({ seconds, isActive, toggle, reset }) => {
    const formatTime = () => {
        const totalSeconds = seconds;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        const formattedHours = String(hours).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(secs).padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow-md h-full flex flex-col justify-center text-center">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Study Timer</h3>
            <div className="text-5xl font-mono text-gray-900 my-4">
                {formatTime()}
            </div>
            <div className="space-x-4">
                <button onClick={toggle} className={`px-6 py-2 font-semibold rounded-lg text-white ${isActive ? 'bg-orange-500 hover:bg-orange-600' : 'bg-pink-500 hover:bg-pink-600'}`}>
                    {isActive ? 'Pause' : 'Start'}
                </button>
                <button onClick={reset} className="px-6 py-2 font-semibold rounded-lg text-white bg-red-500 hover:bg-red-600">
                    Reset
                </button>
            </div>
        </div>
    );
};

// --- Constants ---
const TASK_COLUMNS = ["Class", "Kaplan", "BPP", "Studyhub"];
const CALENDAR_PROVIDERS = ["Kaplan", "BPP", "Studyhub"];

// --- Helper Functions ---
const getSortValue = (probability = '') => {
    const prob = String(probability).toLowerCase().trim();
    if (prob.endsWith('%')) {
        const num = parseFloat(prob);
        if (!isNaN(num)) {
            return 100 - num; // Higher percentages first
        }
    }
    if (prob === 'high') return -3;
    if (prob === 'medium') return -2;
    if (prob === 'low') return -1;
    if (prob === '') return 1000; // Empty values at the bottom
    return prob; // Alphabetical for others
};

const delay = ms => new Promise(res => setTimeout(res, ms));


// --- UI Components ---
const CircularProgressBar = ({ progress, size = 120, strokeWidth = 10 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg className="absolute" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle
                    className="text-gray-200"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="transparent"
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                />
                <circle
                    className="text-pink-600"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    fill="transparent"
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                    style={{
                        strokeDasharray: circumference,
                        strokeDashoffset: offset,
                        transform: 'rotate(-90deg)',
                        transformOrigin: '50% 50%',
                        transition: 'stroke-dashoffset 0.5s ease 0s',
                    }}
                />
            </svg>
            <span className="absolute text-2xl font-bold text-gray-700">{`${Math.round(progress)}%`}</span>
        </div>
    );
};

const ExamCountdown = ({ examDate }) => {
    const calculateDaysRemaining = () => {
        if (!examDate) {
            return { days: null, message: "Exam date not set" };
        }

        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);

        const [year, month, day] = examDate.split('-').map(Number);
        const examDay = new Date(Date.UTC(year, month - 1, day));

        const timeDiff = examDay.getTime() - today.getTime();
        const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

        if (daysRemaining < 0) {
            return { days: daysRemaining, message: "The exam has passed" };
        }
        if (daysRemaining === 0) {
            return { days: 0, message: "Exam is today! Good luck!" };
        }
        if (daysRemaining === 1) {
            return { days: 1, message: "Day Until Exam" };
        }
        return { days: daysRemaining, message: "Days Until Exam" };
    };

    const { days, message } = calculateDaysRemaining();

    if (days === null) {
        return (
            <div className="text-center p-4 bg-gray-100 rounded-lg shadow-inner min-w-[120px]">
                <p className="text-gray-600 font-semibold">{message}</p>
            </div>
        );
    }

    return (
        <div className="text-center p-3 bg-pink-50 rounded-lg shadow-inner border border-pink-200 min-w-[120px]">
            <div className="text-4xl font-bold text-pink-600">{days < 0 ? '—' : days}</div>
            <div className="text-xs font-semibold text-pink-800 uppercase tracking-wider mt-1">{message}</div>
        </div>
    );
};

const MotivationalQuotes = () => {
    const quotes = [
        "Believe you can and you're halfway there.",
        "The secret of getting ahead is getting started.",
        "It’s not whether you get knocked down, it’s whether you get up.",
        "The future belongs to those who believe in the beauty of their dreams.",
        "Success is not final, failure is not fatal: it is the courage to continue that counts.",
        "Don't watch the clock; do what it does. Keep going.",
        "The only place where success comes before work is in the dictionary.",
        "The expert in anything was once a beginner."
    ];

    const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
        }, 5000);

        return () => clearInterval(timer);
    }, [quotes.length]);

    return (
        <div className="mt-6 text-center h-12 flex items-center justify-center">
            <p className="text-md font-bold text-black transition-opacity duration-500">"{quotes[currentQuoteIndex]}"</p>
        </div>
    );
};


// --- User Console ---
const UserConsole = ({ userData }) => {
    const [accessiblePapers, setAccessiblePapers] = useState(Object.entries(userData.accessiblePapers || {}));
    const [viewingPaper, setViewingPaper] = useState(null); // { id: 'paperId', name: 'Paper Name' }

    // --- Timer State and Logic ---
    const [seconds, setSeconds] = useState(0);
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        let interval = null;
        if (isActive) {
            interval = setInterval(() => {
                setSeconds(s => s + 1);
            }, 1000);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isActive]);

    const toggleTimer = () => {
        setIsActive(!isActive);
    };

    const resetTimer = () => {
        setSeconds(0);
        setIsActive(false);
    };
    // --- End Timer State ---

    if (viewingPaper) {
        return <UserPaperTracker
                    paper={viewingPaper}
                    userId={userData.uid}
                    onBack={() => setViewingPaper(null)}
                    timerProps={{ seconds, isActive, toggle: toggleTimer, reset: resetTimer }}
                />;
    }

    return (
        <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-gray-700 mb-6 mt-8">My ACCA Papers</h2>
            {accessiblePapers.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                    <p className="text-gray-500">You have not been assigned any papers yet.</p>
                    <p className="text-gray-500 mt-2">Please contact your administrator.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {accessiblePapers
                        .sort((a, b) => (a[1]?.name || '').localeCompare(b[1]?.name || ''))
                        .map(([paperId, paperData]) => (
                        <div key={paperId} className="bg-white rounded-xl shadow-lg transition-transform transform hover:-translate-y-1">
                            <button onClick={() => setViewingPaper({ id: paperId, name: paperData.name })} className="w-full text-left p-6">
                                <h3 className="font-bold text-lg text-gray-800">{paperData.name}</h3>
                                <p className="text-sm text-pink-600 mt-2">View Study Tracker &rarr;</p>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- Admin & Super Admin Base Components ---
const AdminBase = ({ children, userData }) => {
    const [activeTab, setActiveTab] = useState('manageUsers');
    const [managingPaper, setManagingPaper] = useState(null); // { id, name }

    const tabs = [
        { id: 'manageUsers', label: 'Manage Students' },
        { id: 'managePapers', label: 'Manage Papers' },
        ...(userData.role === 'superadmin' ? [{ id: 'manageAdmins', label: 'Manage Admins' }] : [])
    ];

    if (managingPaper) {
        return <PaperTrackerAdmin paper={managingPaper} onBack={() => setManagingPaper(null)} />;
    }

    return (
        <div className="max-w-7xl mx-auto">
            <div className="mb-6 border-b border-gray-200 mt-8">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`${
                                activeTab === tab.id
                                    ? 'border-pink-500 text-pink-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>
            <div>
                {React.Children.map(children, child =>
                    React.cloneElement(child, {
                        isActive: activeTab === child.props.tabId,
                        onManagePaper: setManagingPaper,
                        adminUserData: userData
                    })
                )}
            </div>
        </div>
    );
};

const AdminConsole = ({ userData }) => (
    <AdminBase userData={userData}>
        <ManageUsers tabId="manageUsers" />
        <ManagePapers tabId="managePapers" />
    </AdminBase>
);

const SuperAdminConsole = ({ userData }) => (
    <AdminBase userData={userData}>
        <ManageUsers tabId="manageUsers" />
        <ManagePapers tabId="managePapers" />
        <ManageAdmins tabId="manageAdmins" />
    </AdminBase>
);


// --- Admin-Focused Components ---

const MultiSelectDropdown = ({ options, selectedOptions, onChange, placeholder = "Select papers..." }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleOptionToggle = (optionId) => {
        if (selectedOptions.includes(optionId)) {
            onChange(selectedOptions.filter(id => id !== optionId));
        } else {
            onChange([...selectedOptions, optionId]);
        }
    };

    return (
        <div className="relative w-full md:w-64" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white text-left flex justify-between items-center"
            >
                <span className="truncate">
                    {selectedOptions.length > 0 ? `${selectedOptions.length} papers selected` : placeholder}
                </span>
                 <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>
            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg border rounded-md max-h-60 overflow-y-auto">
                    <ul>
                        {options.map(option => (
                            <li key={option.id} className="p-2 hover:bg-gray-100">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedOptions.includes(option.id)}
                                        onChange={() => handleOptionToggle(option.id)}
                                        className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                                    />
                                    <span className="text-sm text-gray-700">{option.name}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

const ManageUsers = ({ isActive, adminUserData }) => {
    const [users, setUsers] = useState([]);
    const [allPapers, setAllPapers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
    const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isProcessing, setIsProcessing] = useState(null);
    const [viewingReport, setViewingReport] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [reportProgress, setReportProgress] = useState(0);
    const [deletingUser, setDeletingUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPaperFilters, setSelectedPaperFilters] = useState([]);
    const [showUnassigned, setShowUnassigned] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);

    const fetchUsersAndPapers = useCallback(async () => {
        setIsLoading(true);
        try {
            const usersQuery = query(collection(db, "users"), where("role", "==", "user"));
            const papersQuery = query(collection(db, "papers"), orderBy("name"));

            const [usersSnapshot, papersSnapshot] = await Promise.all([
                getDocs(usersQuery),
                getDocs(papersQuery)
            ]);

            const usersList = usersSnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
            let papersList = papersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (adminUserData.role === 'admin') {
                const adminPaperIds = Object.keys(adminUserData.accessiblePapers || {});
                papersList = papersList.filter(p => adminPaperIds.includes(p.id));
            }

            setUsers(usersList);
            setAllPapers(papersList);

        } catch (error) {
            console.error("Error fetching data: ", error);
        }
        setIsLoading(false);
    }, [adminUserData]);

    useEffect(() => {
        if (isActive) {
            fetchUsersAndPapers();
        }
    }, [isActive, fetchUsersAndPapers]);

    const handleAddUser = () => {
        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUsers(filteredUsers.map(u => u.uid));
        } else {
            setSelectedUsers([]);
        }
    };

    const confirmDeleteUser = async (userId) => {
        try {
            await deleteDoc(doc(db, "users", userId));
            console.log(`User document for ${userId} deleted. Use bulk delete to remove the login account permanently.`);
            setDeletingUser(null);
            fetchUsersAndPapers();
        } catch (error) {
            console.error("Error deleting user document: ", error);
        }
    };

    const handleBulkAssign = async (papersToAssign) => {
        if (selectedUsers.length === 0 || Object.keys(papersToAssign).length === 0) {
            return;
        }
        setIsProcessing('bulk-assign');
        try {
            const batch = writeBatch(db);
            const userDocs = await Promise.all(
                selectedUsers.map(uid => getDoc(doc(db, 'users', uid)))
            );

            userDocs.forEach(userDoc => {
                if(userDoc.exists()) {
                    const userData = userDoc.data();
                    const currentPapers = userData.accessiblePapers || {};
                    const updatedPapers = { ...currentPapers, ...papersToAssign };
                    batch.update(userDoc.ref, { accessiblePapers: updatedPapers });
                }
            });

            await batch.commit();
            fetchUsersAndPapers();
            setSelectedUsers([]);
        } catch (error) {
            console.error("Error during bulk assignment: ", error);
        }
        setIsProcessing(null);
        setIsBulkAssignModalOpen(false);
    };

    const handleBulkDelete = async () => {
        if (selectedUsers.length === 0) {
            return;
        }

        setIsProcessing('bulk-delete');
        setIsBulkDeleteModalOpen(false);

        try {
            const bulkDeleteUsers = httpsCallable(functions, 'bulkDeleteUsers');
            const result = await bulkDeleteUsers({ uids: selectedUsers });
            
            console.log('Cloud function result:', result.data);
            alert(`${result.data.successCount} users were permanently deleted.`);

            fetchUsersAndPapers();
            setSelectedUsers([]);
        } catch (error) {
            console.error("Error calling bulk delete function:", error);
            alert(`An error occurred: ${error.message}`);
        }
        setIsProcessing(null);
    };

    const calculateOverallStudentProgress = async (user) => {
        let totalTasks = 0;
        let completedTasks = 0;
        const userProgressData = user.accessiblePapers || {};

        for (const paperId in userProgressData) {
            if (Object.hasOwnProperty.call(userProgressData, paperId)) {
                const paperData = userProgressData[paperId];
                const sectionsRef = collection(db, 'papers', paperId, 'sections');
                const sectionsSnapshot = await getDocs(sectionsRef);

                for (const sectionDoc of sectionsSnapshot.docs) {
                    const topicsRef = collection(db, 'papers', paperId, 'sections', sectionDoc.id, 'topics');
                    const topicsSnapshot = await getDocs(topicsRef);

                    for (const topicDoc of topicsSnapshot.docs) {
                        const tasksRef = collection(db, 'papers', paperId, 'sections', sectionDoc.id, 'topics', topicDoc.id, 'tasks');
                        const tasksSnapshot = await getDocs(tasksRef);

                        for (const taskDoc of tasksSnapshot.docs) {
                            const task = { id: taskDoc.id, ...taskDoc.data() };
                            const userTaskProgress = paperData.progress?.[task.id] || {};
                            if (task.column === 'Class') {
                                totalTasks++;
                                if (userTaskProgress.main?.checked) completedTasks++;
                            } else {
                                ['A', 'B', 'C'].forEach(grade => {
                                    if (task.grades && task.grades[grade]) {
                                        totalTasks++;
                                        if (userTaskProgress[grade]?.checked) completedTasks++;
                                    }
                                });
                            }
                        }
                    }
                }
            }
        }
        return totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    };

    const generateReportData = async (user) => {
        const reportData = [];
        const userProgressData = user.accessiblePapers || {};

        for (const paperId in userProgressData) {
            if (Object.hasOwnProperty.call(userProgressData, paperId)) {
                const paperData = userProgressData[paperId];
                const paperName = paperData.name;

                const sectionsRef = collection(db, 'papers', paperId, 'sections');
                const sectionsSnapshot = await getDocs(query(sectionsRef, orderBy("createdAt")));

                for (const sectionDoc of sectionsSnapshot.docs) {
                    const section = { id: sectionDoc.id, ...sectionDoc.data() };
                    const topicsRef = collection(db, 'papers', paperId, 'sections', section.id, 'topics');
                    const topicsSnapshot = await getDocs(query(topicsRef, orderBy("createdAt")));

                    for (const topicDoc of topicsSnapshot.docs) {
                        const topic = { id: topicDoc.id, ...topicDoc.data() };
                        const tasksRef = collection(db, 'papers', paperId, 'sections', section.id, 'topics', topic.id, 'tasks');
                        const tasksSnapshot = await getDocs(query(tasksRef, orderBy("createdAt")));

                        for (const taskDoc of tasksSnapshot.docs) {
                            const task = { id: taskDoc.id, ...taskDoc.data() };
                            const userTaskProgress = paperData.progress?.[task.id] || {};

                            if (task.column === 'Class') {
                                if (!userTaskProgress.main?.checked) {
                                    reportData.push({ paper: paperName, section: section.name, topic: topic.name, task: task.name, details: 'Class', status: 'Pending' });
                                 }
                            } else {
                                ['A', 'B', 'C'].forEach(grade => {
                                    if (task.grades && task.grades[grade]) {
                                        if (!userTaskProgress[grade]?.checked) {
                                            const details = `${task.column} - Grade ${grade} (${task.grades[grade]})`;
                                            reportData.push({ paper: paperName, section: section.name, topic: topic.name, task: task.name, details: details, status: 'Pending' });
                                        }
                                    }
                                });
                            }
                        }
                    }
                }
            }
        }
        return reportData;
    };

    const handleViewReport = async (user) => {
        setIsProcessing(user.uid);
        const data = await generateReportData(user);
        const progress = await calculateOverallStudentProgress(user);
        setReportData(data);
        setReportProgress(progress);
        setViewingReport(user);
        setIsProcessing(null);
    };

    const handleDownloadReport = async (user) => {
        setIsProcessing(user.uid);
        const data = await generateReportData(user);

        const escapeCsv = (str) => `"${(str || '').replace(/"/g, '""')}"`;
        let csvContent = "Paper,Section,Topic,Task,Details,Status\n";

        if (data.length === 0) {
            csvContent += "This student has completed all assigned tasks. Great job!\n";
        } else {
            data.forEach(row => {
                csvContent += `${escapeCsv(row.paper)},${escapeCsv(row.section)},${escapeCsv(row.topic)},${escapeCsv(row.task)},${escapeCsv(row.details)},${escapeCsv(row.status)}\n`;
            });
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `${user.name}_progress_report.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setIsProcessing(null);
    };

    const runAuthCheck = async () => {
      console.log("--- Client: Attempting to run Auth Check ---");
      console.log("--- Client: Current user is:", auth.currentUser);
      try {
        const checkAuthContext = httpsCallable(functions, 'checkAuthContext');
        const result = await checkAuthContext();
        console.log("--- Client: Result from checkAuthContext function ---", result.data);
        alert("Auth Check Server Result: " + result.data.status);
      } catch (error) {
        console.error("--- Client: Error calling checkAuthContext ---", error);
        alert("Error during auth check: " + error.message);
      }
    };
    
    const forceTokenRefresh = async () => {
        console.log("--- Client: Forcing token refresh... ---");
        if (auth.currentUser) {
            try {
                const freshToken = await auth.currentUser.getIdToken(true); // 'true' forces refresh
                console.log("--- Client: Got fresh token ---", freshToken);
                alert("Token has been refreshed. Now try the 'Run Auth Check' button again.");
            } catch (error) {
                console.error("--- Client: Error refreshing token ---", error);
                alert("Could not refresh token. Check the console.");
            }
        } else {
            alert("No user is logged in to refresh.");
        }
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = (user.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                                (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (selectedPaperFilters.length === 0) {
            return true;
        }

        const userPaperIds = Object.keys(user.accessiblePapers || {});

        if (showUnassigned) {
            const hasNoneOfSelected = selectedPaperFilters.every(filterId => !userPaperIds.includes(filterId));
            return hasNoneOfSelected;
        } else {
            const hasAnyOfSelected = selectedPaperFilters.some(filterId => userPaperIds.includes(filterId));
            return hasAnyOfSelected;
        }
    });

    if (!isActive) return null;

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-y-4">
                <h3 className="text-xl font-semibold text-gray-800">Student List</h3>
                <div className="flex items-center space-x-4 flex-wrap gap-2">
                    <button onClick={forceTokenRefresh} className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg">
                        Force Refresh Token
                    </button>
                    <button onClick={runAuthCheck} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded-lg">
                        Run Auth Check
                    </button>
                    {selectedUsers.length > 0 && (
                        <>
                            <button onClick={() => setIsBulkAssignModalOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition">
                                Bulk Assign ({selectedUsers.length})
                            </button>
                            <button onClick={() => setIsBulkDeleteModalOpen(true)} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition">
                                Bulk Delete ({selectedUsers.length})
                            </button>
                        </>
                    )}
                     <button onClick={handleAddUser} className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg transition self-center">
                        Add Student
                    </button>
                    <button onClick={() => setIsCsvModalOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-lg transition">
                        Upload CSV
                    </button>
                </div>
            </div>

            <div className="mb-4 flex flex-wrap gap-4 items-center">
                <div className="relative flex-grow">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                    </span>
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                </div>
                 <MultiSelectDropdown
                    options={allPapers}
                    selectedOptions={selectedPaperFilters}
                    onChange={setSelectedPaperFilters}
                    placeholder="Filter by papers..."
                />
                <div className="flex items-center">
                    <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showUnassigned}
                            onChange={(e) => setShowUnassigned(e.target.checked)}
                            className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                            disabled={selectedPaperFilters.length === 0}
                        />
                        <span className={`text-sm text-gray-700 ${selectedPaperFilters.length === 0 ? 'text-gray-400' : ''}`}>
                            Show unassigned
                        </span>
                    </label>
                </div>
            </div>

            {isLoading ? (
                <p>Loading students...</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={selectedUsers.length > 0 && filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                                        className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                                    />
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paper Access</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map(user => (
                                <tr key={user.uid} className={selectedUsers.includes(user.uid) ? 'bg-pink-50' : ''}>
                                    <td className="px-6 py-4">
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(user.uid)}
                                            onChange={() => handleSelectUser(user.uid)}
                                            className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Object.keys(user.accessiblePapers || {}).length} papers</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                        <button
                                            onClick={() => handleViewReport(user)}
                                            className="text-purple-600 hover:text-purple-900 disabled:text-gray-400"
                                            disabled={isProcessing === user.uid}
                                        >
                                            {isProcessing === user.uid ? '...' : 'View'}
                                        </button>
                                        <button
                                            onClick={() => handleDownloadReport(user)}
                                            className="text-green-600 hover:text-green-900 disabled:text-gray-400"
                                            disabled={isProcessing === user.uid}
                                        >
                                            {isProcessing === user.uid ? '...' : 'Download'}
                                        </button>
                                        <button onClick={() => handleEditUser(user)} className="text-orange-600 hover:text-orange-900">Edit</button>
                                        <button onClick={() => setDeletingUser(user)} className="text-red-600 hover:text-red-900">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredUsers.length === 0 && !isLoading && (
                        <div className="text-center py-8 text-gray-500">
                            <p>No students found matching your criteria.</p>
                        </div>
                    )}
                </div>
            )}
            {isModalOpen && <UserForm user={editingUser} onClose={() => setIsModalOpen(false)} onSave={fetchUsersAndPapers} adminUserData={adminUserData} />}
            {isCsvModalOpen && <CsvUploadModal onClose={() => setIsCsvModalOpen(false)} onUploadComplete={fetchUsersAndPapers} />}
            {isBulkAssignModalOpen && <BulkAssignModal allPapers={allPapers} onAssign={handleBulkAssign} onClose={() => setIsBulkAssignModalOpen(false)} isLoading={isProcessing === 'bulk-assign'} />}
            {viewingReport && <ReportModal user={viewingReport} reportData={reportData} progress={reportProgress} onClose={() => setViewingReport(null)} />}
            {deletingUser && <ConfirmDeleteModal user={deletingUser} onConfirm={() => confirmDeleteUser(deletingUser.uid)} onCancel={() => setDeletingUser(null)} />}
            {isBulkDeleteModalOpen && (
                <ConfirmModal
                    title="Confirm Permanent Deletion"
                    message={`Are you sure you want to permanently delete the ${selectedUsers.length} selected students? This will remove their login accounts and all their data. This action is irreversible.`}
                    onConfirm={() => handleBulkDelete()}
                    onCancel={() => setIsBulkDeleteModalOpen(false)}
                    confirmText="Permanently Delete"
                />
            )}
        </div>
    );
};
const ManageAdmins = ({ isActive }) => {
    const [admins, setAdmins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [deletingAdmin, setDeletingAdmin] = useState(null);

    const fetchAdmins = useCallback(async () => {
        setIsLoading(true);
        try {
            const q = query(collection(db, "users"), where("role", "==", "admin"));
            const querySnapshot = await getDocs(q);
            const adminsList = querySnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
            setAdmins(adminsList);
        } catch (error) {
            console.error("Error fetching admins: ", error);
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (isActive) {
            fetchAdmins();
        }
    }, [isActive, fetchAdmins]);

    const handleAddAdmin = () => {
        setEditingAdmin(null);
        setIsModalOpen(true);
    };

    const handleEditAdmin = (admin) => {
        setEditingAdmin(admin);
        setIsModalOpen(true);
    };

    const confirmDeleteAdmin = async (adminId) => {
         try {
            await deleteDoc(doc(db, "users", adminId));
            console.log(`Admin document for ${adminId} deleted. Auth user deletion would be handled by a Cloud Function.`);
            setDeletingAdmin(null);
            fetchAdmins();
        } catch (error) {
            console.error("Error deleting admin: ", error);
        }
    };

    if (!isActive) return null;

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">Admin List</h3>
                <button onClick={handleAddAdmin} className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg transition">
                    Add Admin
                </button>
            </div>
            {isLoading ? (
                <p>Loading admins...</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paper Access</th>
                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {admins.map(admin => (
                                <tr key={admin.uid}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{admin.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Object.keys(admin.accessiblePapers || {}).length} papers</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                                        <button onClick={() => handleEditAdmin(admin)} className="text-orange-600 hover:text-orange-900">Edit</button>
                                        <button onClick={() => setDeletingAdmin(admin)} className="text-red-600 hover:text-red-900">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {isModalOpen && <AdminForm admin={editingAdmin} onClose={() => setIsModalOpen(false)} onSave={fetchAdmins} />}
            {deletingAdmin && <ConfirmDeleteModal user={deletingAdmin} onConfirm={() => confirmDeleteAdmin(deletingAdmin.uid)} onCancel={() => setDeletingAdmin(null)} />}
        </div>
    );
};

const ManagePapers = ({ isActive, onManagePaper, adminUserData }) => {
    const [papers, setPapers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPaper, setEditingPaper] = useState(null);
    const [deletingPaper, setDeletingPaper] = useState(null);
    const [duplicatingPaper, setDuplicatingPaper] = useState(null); // New state for duplicating

    const fetchPapers = useCallback(async () => {
        setIsLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, "papers"));
            let papersList = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => a.name.localeCompare(b.name));

            if (adminUserData.role === 'admin') {
                const adminPaperIds = Object.keys(adminUserData.accessiblePapers || {});
                papersList = papersList.filter(p => adminPaperIds.includes(p.id));
            }

            setPapers(papersList);
        } catch (error) {
            console.error("Error fetching papers: ", error);
        }
        setIsLoading(false);
    }, [adminUserData]);

    useEffect(() => {
        if (isActive) {
            fetchPapers();
        }
    }, [isActive, fetchPapers]);

    const handleAddPaper = () => {
        setEditingPaper(null);
        setDuplicatingPaper(null);
        setIsModalOpen(true);
    };

    const handleEditPaper = (paper) => {
        setEditingPaper(paper);
        setDuplicatingPaper(null);
        setIsModalOpen(true);
    };

    const handleDuplicatePaper = (paper) => {
        setDuplicatingPaper(paper);
        setEditingPaper(null);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingPaper(null);
        setDuplicatingPaper(null);
    };

    const confirmDeletePaper = async () => {
        if (!deletingPaper) return;

        setIsLoading(true);
        try {
            const paperId = deletingPaper.id;
            const batch = writeBatch(db);

            // Query all users to remove the paper access
            const usersSnapshot = await getDocs(collection(db, "users"));
            usersSnapshot.forEach((userDoc) => {
                const userData = userDoc.data();
                if (userData.accessiblePapers && userData.accessiblePapers[paperId]) {
                    const { [paperId]: _, ...remainingPapers } = userData.accessiblePapers;
                    batch.update(userDoc.ref, { accessiblePapers: remainingPapers });
                }
            });

            // Delete the paper document
            const paperRef = doc(db, "papers", paperId);
            batch.delete(paperRef);

            // Commit all batched writes
            await batch.commit();

            setDeletingPaper(null);
            // Fetch the updated list of papers, which will also set isLoading to false
            fetchPapers();
        } catch (error) {
            console.error("Error deleting paper: ", error);
            setDeletingPaper(null);
            setIsLoading(false); // Ensure loading is turned off on error
        }
    };

    if (!isActive) return null;

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">Paper List</h3>
                {adminUserData.role === 'superadmin' && (
                    <button onClick={handleAddPaper} className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg transition">
                        Add New Paper
                    </button>
                )}
            </div>
            {isLoading ? (
                <p>Loading papers...</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paper Name</th>
                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {papers.map(paper => (
                                <tr key={paper.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{paper.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                                        <button onClick={() => onManagePaper(paper)} disabled={isLoading} className="text-green-600 hover:text-green-900 font-semibold disabled:text-gray-400">Manage Tracker</button>
                                        {adminUserData.role === 'superadmin' && (
                                            <>
                                                <button onClick={() => handleDuplicatePaper(paper)} disabled={isLoading} className="text-blue-600 hover:text-blue-900 disabled:text-gray-400">Duplicate</button>
                                                <button onClick={() => handleEditPaper(paper)} disabled={isLoading} className="text-orange-600 hover:text-orange-900 disabled:text-gray-400">Rename</button>
                                                <button onClick={() => setDeletingPaper(paper)} disabled={isLoading} className="text-red-600 hover:text-red-900 disabled:text-gray-400">Delete</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {isModalOpen && <PaperForm paper={editingPaper} duplicateSource={duplicatingPaper} onClose={handleModalClose} onSave={fetchPapers} />}
            {deletingPaper && (
                <ConfirmModal
                    title="Confirm Paper Deletion"
                    message={`Are you sure you want to delete the paper "${deletingPaper.name}"? This will remove it for all users and cannot be undone.`}
                    onConfirm={confirmDeletePaper}
                    onCancel={() => setDeletingPaper(null)}
                    confirmText="Delete"
                />
            )}
        </div>
    );
};
// --- Modals and Forms ---
const ConfirmModal = ({ title, message, onConfirm, onCancel, confirmText = "Confirm", cancelText = "Cancel" }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{title}</h3>
                <p className="text-gray-600">{message}</p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                <button onClick={onCancel} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">{cancelText}</button>
                <button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg">{confirmText}</button>
            </div>
        </div>
    </div>
);

const ConfirmDeleteModal = ({ user, onConfirm, onCancel }) => (
    <ConfirmModal
        title="Confirm Deletion"
        message={`Are you sure you want to delete the user ${user.name}? This action will only remove the user's data from the tracker. A backend function is required to delete their login account permanently.`}
        onConfirm={onConfirm}
        onCancel={onCancel}
        confirmText="Delete"
    />
);

const ReportModal = ({ user, reportData, progress, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="p-6 border-b flex justify-between items-center">
                    <h3 className="text-xl font-semibold">Pending Report for {user.name}</h3>
                    <div className="text-center">
                        <CircularProgressBar progress={progress} size={80} strokeWidth={8} />
                        <p className="text-xs font-semibold mt-1">Overall Progress</p>
                    </div>
                </div>
                <div className="p-6 flex-grow overflow-y-auto">
                    {reportData.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paper</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Topic</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {reportData.map((row, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm">{row.paper}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm">{row.section}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm">{row.topic}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm">{row.task}</td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm">{row.details}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="text-center text-gray-500 py-8">This student has completed all assigned tasks. Great job!</p>
                    )}
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                    <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Close</button>
                </div>
            </div>
        </div>
    );
};

const BulkAssignModal = ({ allPapers, onAssign, onClose, isLoading }) => {
    const [selectedPapers, setSelectedPapers] = useState({});

    const handlePaperToggle = (paperId, paperName) => {
        const newPapers = { ...selectedPapers };
        if (newPapers[paperId]) {
            delete newPapers[paperId];
        } else {
            newPapers[paperId] = { name: paperName, progress: {} };
        }
        setSelectedPapers(newPapers);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onAssign(selectedPapers);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-y-auto">
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold mb-4">Bulk Assign Papers</h3>
                        <p className="text-sm text-gray-600 mb-4">Select the papers you want to assign to the selected students. This will add the papers and will not remove existing ones.</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-4 border rounded-lg max-h-64 overflow-y-auto">
                            {allPapers.map(paper => (
                                <div key={paper.id} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id={`bulk-paper-${paper.id}`}
                                        checked={!!selectedPapers[paper.id]}
                                        onChange={() => handlePaperToggle(paper.id, paper.name)}
                                        className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                                    />
                                    <label htmlFor={`bulk-paper-${paper.id}`} className="ml-2 block text-sm text-gray-900">{paper.name}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isLoading || Object.keys(selectedPapers).length === 0} className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-pink-300">
                            {isLoading ? 'Assigning...' : 'Assign Papers'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const UserForm = ({ user, onClose, onSave, adminUserData }) => {
    const [name, setName] = useState(user ? user.name : '');
    const [email, setEmail] = useState(user ? user.email : '');
    const [password, setPassword] = useState('');
    const [accessiblePapers, setAccessiblePapers] = useState(user ? user.accessiblePapers || {} : {});
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [allPapers, setAllPapers] = useState([]);
    const [papersLoading, setPapersLoading] = useState(true);

    useEffect(() => {
        const fetchPapers = async () => {
            setPapersLoading(true);
            try {
                const querySnapshot = await getDocs(collection(db, "papers"));
                let papersList = querySnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => a.name.localeCompare(b.name));

                if (adminUserData.role === 'admin') {
                    const adminPaperIds = Object.keys(adminUserData.accessiblePapers || {});
                    papersList = papersList.filter(p => adminPaperIds.includes(p.id));
                }

                setAllPapers(papersList);
            } catch (error) {
                console.error("Failed to fetch papers for form", error);
                setError("Could not load paper list.");
            }
            setPapersLoading(false);
        };
        fetchPapers();
    }, [adminUserData]);

    const handlePaperToggle = (paperId, paperName) => {
        const newPapers = { ...accessiblePapers };
        if (newPapers[paperId]) {
            delete newPapers[paperId];
        } else {
            newPapers[paperId] = { name: paperName, progress: {} };
        }
        setAccessiblePapers(newPapers);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            if (user) {
                const userDocRef = doc(db, 'users', user.uid);
                await updateDoc(userDocRef, { name, accessiblePapers });
            } else {
                // Use a temporary, secondary Firebase app instance for user creation
                // to avoid logging out the current admin user.
                const tempAppName = `Secondary-${Date.now()}`;
                const tempApp = initializeApp(firebaseConfig, tempAppName);
                const tempAuth = getAuth(tempApp);
                const userCredential = await createUserWithEmailAndPassword(tempAuth, email, password);
                const newUser = userCredential.user;

                await setDoc(doc(db, 'users', newUser.uid), {
                    name,
                    email,
                    role: 'user',
                    accessiblePapers,
                });
                 await signOut(tempAuth);
            }
            onSave();
            onClose();
        } catch (err) {
            console.error("Form submission error:", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-y-auto">
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold mb-4">{user ? 'Edit Student' : 'Add New Student'}</h3>
                        {error && <p className="text-red-500 mb-4">{error}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                <input type="text" value={name} onChange={e => setName(e.target.value)} required className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required disabled={!!user} className="mt-1 block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm" />
                            </div>
                            {!user && (
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Password</label>
                                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm" />
                                </div>
                            )}
                        </div>
                        <div className="mt-6">
                            <h4 className="text-lg font-medium text-gray-800 mb-2">Paper Access</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-4 border rounded-lg max-h-64 overflow-y-auto">
                                {papersLoading ? <p>Loading papers...</p> : allPapers.map(paper => (
                                    <div key={paper.id} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`paper-access-${paper.id}`}
                                            checked={!!accessiblePapers[paper.id]}
                                            onChange={() => handlePaperToggle(paper.id, paper.name)}
                                            className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                                        />
                                        <label htmlFor={`paper-access-${paper.id}`} className="ml-2 block text-sm text-gray-900">{paper.name}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isLoading} className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-pink-300">{isLoading ? 'Saving...' : 'Save'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AdminForm = ({ admin, onClose, onSave }) => {
    const [name, setName] = useState(admin ? admin.name : '');
    const [email, setEmail] = useState(admin ? admin.email : '');
    const [password, setPassword] = useState('');
    const [accessiblePapers, setAccessiblePapers] = useState(admin ? admin.accessiblePapers || {} : {});
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [allPapers, setAllPapers] = useState([]);
    const [papersLoading, setPapersLoading] = useState(true);

    useEffect(() => {
        const fetchPapers = async () => {
            setPapersLoading(true);
            try {
                const querySnapshot = await getDocs(collection(db, "papers"));
                const papersList = querySnapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                setAllPapers(papersList);
            } catch (error) {
                console.error("Failed to fetch papers for form", error);
                setError("Could not load paper list.");
            }
            setPapersLoading(false);
        };
        fetchPapers();
    }, []);

    const handlePaperToggle = (paperId, paperName) => {
        const newPapers = { ...accessiblePapers };
        if (newPapers[paperId]) {
            delete newPapers[paperId];
        } else {
            newPapers[paperId] = { name: paperName };
        }
        setAccessiblePapers(newPapers);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            if (admin) {
                const adminDocRef = doc(db, 'users', admin.uid);
                await updateDoc(adminDocRef, { name, accessiblePapers });
            } else {
                const tempAppName = `SecondaryAdmin-${Date.now()}`;
                const tempApp = initializeApp(firebaseConfig, tempAppName);
                const tempAuth = getAuth(tempApp);
                const userCredential = await createUserWithEmailAndPassword(tempAuth, email, password);
                const newAdmin = userCredential.user;

                await setDoc(doc(db, 'users', newAdmin.uid), {
                    name,
                    email,
                    role: 'admin',
                    accessiblePapers,
                });
                await signOut(tempAuth);
            }
            onSave();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-y-auto">
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold mb-4">{admin ? 'Edit Admin' : 'Add New Admin'}</h3>
                        {error && <p className="text-red-500 mb-4">{error}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                <input type="text" value={name} onChange={e => setName(e.target.value)} required className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required disabled={!!admin} className="mt-1 block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md shadow-sm" />
                            </div>
                            {!admin && (
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Password</label>
                                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm" />
                                </div>
                            )}
                        </div>
                        <div className="mt-6">
                            <h4 className="text-lg font-medium text-gray-800 mb-2">Paper Access</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-4 border rounded-lg max-h-64 overflow-y-auto">
                                {papersLoading ? <p>Loading papers...</p> : allPapers.map(paper => (
                                    <div key={paper.id} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`paper-access-admin-${paper.id}`}
                                            checked={!!accessiblePapers[paper.id]}
                                            onChange={() => handlePaperToggle(paper.id, paper.name)}
                                            className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                                        />
                                        <label htmlFor={`paper-access-admin-${paper.id}`} className="ml-2 block text-sm text-gray-900">{paper.name}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isLoading} className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-pink-300">{isLoading ? 'Saving...' : 'Save Admin'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const PaperForm = ({ paper, duplicateSource, onClose, onSave }) => {
    // If duplicating, append "(Copy)" to the default name
    const defaultName = paper ? paper.name : (duplicateSource ? `${duplicateSource.name} (Copy)` : '');
    const [name, setName] = useState(defaultName);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            setError("Paper name cannot be empty.");
            return;
        }
        setIsLoading(true);
        setError('');

        try {
            if (paper) {
                // UPDATE EXISTING PAPER
                const paperDocRef = doc(db, 'papers', paper.id);
                await updateDoc(paperDocRef, { name });
            } else if (duplicateSource) {
                // DUPLICATE EXISTING PAPER
                setProgressMessage('Creating new paper instance...');
                
                // Fetch optional top-level data from the source (like examDate)
                const sourceDocRef = doc(db, 'papers', duplicateSource.id);
                const sourcePaperSnap = await getDoc(sourceDocRef);
                const sourceData = sourcePaperSnap.exists() ? sourcePaperSnap.data() : {};

                // 1. Create the new root paper document
                const newPaperRef = await addDoc(collection(db, 'papers'), { 
                    name, 
                    createdAt: new Date(),
                    examDate: sourceData.examDate || null
                });
                
                const newPaperId = newPaperRef.id;

                setProgressMessage('Copying sections, topics, and tasks...');
                
                // 2. Fetch and recursively copy all sections, topics, and tasks
                const sectionsRef = collection(db, 'papers', duplicateSource.id, 'sections');
                const sectionsSnap = await getDocs(sectionsRef);

                for (const sectionDoc of sectionsSnap.docs) {
                    const sectionData = sectionDoc.data();
                    const newSectionRef = await addDoc(collection(db, 'papers', newPaperId, 'sections'), {
                        ...sectionData,
                        createdAt: new Date()
                    });

                    const topicsRef = collection(db, 'papers', duplicateSource.id, 'sections', sectionDoc.id, 'topics');
                    const topicsSnap = await getDocs(topicsRef);

                    for (const topicDoc of topicsSnap.docs) {
                        const topicData = topicDoc.data();
                        const newTopicRef = await addDoc(collection(db, 'papers', newPaperId, 'sections', newSectionRef.id, 'topics'), {
                            ...topicData,
                            createdAt: new Date()
                        });

                        const tasksRef = collection(db, 'papers', duplicateSource.id, 'sections', sectionDoc.id, 'topics', topicDoc.id, 'tasks');
                        const tasksSnap = await getDocs(tasksRef);

                        for (const taskDoc of tasksSnap.docs) {
                            const taskData = taskDoc.data();
                            await addDoc(collection(db, 'papers', newPaperId, 'sections', newSectionRef.id, 'topics', newTopicRef.id, 'tasks'), {
                                ...taskData,
                                createdAt: new Date()
                            });
                        }
                    }
                }
            } else {
                // CREATE BRAND NEW PAPER
                await addDoc(collection(db, 'papers'), { name, createdAt: new Date() });
            }
            onSave();
            onClose();
        } catch (err) {
            console.error("Paper form submission error:", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
            setProgressMessage('');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold mb-4">
                            {paper ? 'Rename Paper' : (duplicateSource ? 'Duplicate Paper' : 'Add New Paper')}
                        </h3>
                        {error && <p className="text-red-500 mb-4">{error}</p>}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Paper Name</label>
                                <input 
                                    type="text" 
                                    value={name} 
                                    onChange={e => setName(e.target.value)} 
                                    required 
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm" 
                                />
                            </div>
                            {isLoading && progressMessage && (
                                <p className="text-sm text-pink-600 font-medium animate-pulse">{progressMessage}</p>
                            )}
                        </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <button type="button" onClick={onClose} disabled={isLoading} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg disabled:opacity-50">Cancel</button>
                        <button type="submit" disabled={isLoading} className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-pink-300">
                            {isLoading ? 'Processing...' : (duplicateSource ? 'Duplicate Paper' : 'Save Paper')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
const CsvUploadModal = ({ onClose, onUploadComplete }) => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [logs, setLogs] = useState([]);

    useEffect(() => {
        if (window.Papa) return;

        const script = document.createElement('script');
        script.id = 'papaparse-script';
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.2/papaparse.min.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            const scriptTag = document.getElementById('papaparse-script');
            if (scriptTag) {
                document.body.removeChild(scriptTag);
            }
        };
    }, []);


    const addLog = (message, type = 'info') => {
        setLogs(prev => [...prev, { message, type, time: new Date().toLocaleTimeString() }]);
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const processCsv = async () => {
        if (!file) {
            addLog('No file selected.', 'error');
            return;
        }

        if (typeof window.Papa === 'undefined') {
            addLog('CSV parsing library is still loading. Please try again in a moment.', 'warn');
            return;
        }

        setIsProcessing(true);
        setLogs([]);
        setProgress(0);
        addLog(`Starting processing for ${file.name}...`, 'info');

        window.Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                const usersToCreate = results.data;
                addLog(`Found ${usersToCreate.length} rows in CSV.`, 'info');

                const requiredHeaders = ['name', 'email', 'password', 'paper_access'];
                const headers = results.meta.fields;
                if (!requiredHeaders.every(h => headers.includes(h))) {
                    addLog(`CSV validation failed. Required headers are: ${requiredHeaders.join(', ')}`, 'error');
                    setIsProcessing(false);
                    return;
                }

                let availablePapers = [];
                try {
                    addLog('Fetching available papers from database...', 'info');
                    const papersSnapshot = await getDocs(collection(db, "papers"));
                    availablePapers = papersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    addLog(`Found ${availablePapers.length} papers.`, 'info');
                } catch (e) {
                    addLog('Fatal: Could not fetch paper list from database. Aborting.', 'error');
                    setIsProcessing(false);
                    return;
                }

                const tempAppName = `CsvUploader-${Date.now()}`;
                const tempApp = initializeApp(firebaseConfig, tempAppName);
                const tempAuth = getAuth(tempApp);

                for (let i = 0; i < usersToCreate.length; i++) {
                    const row = usersToCreate[i];
                    setProgress(((i + 1) / usersToCreate.length) * 100);

                    if (!row.email || !row.password || !row.name) {
                        addLog(`Skipping row ${i + 2}: Missing required fields (name, email, password).`, 'warn');
                        continue;
                    }

                    try {
                        const q = query(collection(db, "users"), where("email", "==", row.email));
                        const existingUser = await getDocs(q);
                        if (!existingUser.empty) {
                            addLog(`Skipping row ${i + 2}: Email ${row.email} already exists.`, 'warn');
                            continue;
                        }

                        const userCredential = await createUserWithEmailAndPassword(tempAuth, row.email, row.password);
                        const newUser = userCredential.user;

                        const accessiblePapers = {};
                        if (row.paper_access) {
                            const papersFromCsv = row.paper_access.split(',').map(p => p.trim());
                            papersFromCsv.forEach(paperName => {
                                const foundPaper = availablePapers.find(p => p.name === paperName);
                                if (foundPaper) {
                                    accessiblePapers[foundPaper.id] = { name: foundPaper.name, progress: {} };
                                } else {
                                    addLog(`Warning for ${row.email}: Paper "${paperName}" not found and was not assigned.`, 'warn');
                                }
                            });
                        }

                        await setDoc(doc(db, 'users', newUser.uid), {
                            name: row.name,
                            email: row.email,
                            role: 'user',
                            accessiblePapers,
                        });

                        addLog(`Successfully created user for ${row.email}.`, 'success');

                    } catch (error) {
                        addLog(`Error on row ${i + 2} (${row.email}): ${error.message}`, 'error');
                        // If we hit the rate limit, pause and retry the same user.
                        if (error.code === 'auth/too-many-requests') {
                            addLog('Rate limit hit. Pausing for 5 seconds before retrying...', 'warn');
                            await delay(5000); // Wait 5 seconds
                            i--; // Decrement 'i' to retry the current row in the next iteration.
                            continue; // Skip the small delay at the end of the loop
                        }
                    }
                    // Add a small delay between each request to avoid hitting rate limits.
                    await delay(500);
                }

                await signOut(tempAuth);
                addLog('Processing complete!', 'info');
                setIsProcessing(false);
                onUploadComplete();
            }
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                <div className="p-6 border-b">
                    <h3 className="text-xl font-semibold">Bulk Upload Students via CSV</h3>
                    <p className="text-sm text-gray-500 mt-1">Headers: name, email, password, paper_access. For `paper_access`, use full paper names separated by commas.</p>
                </div>
                <div className="p-6 space-y-4 flex-grow overflow-y-auto">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">CSV File</label>
                        <input type="file" accept=".csv" onChange={handleFileChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100"/>
                    </div>
                    {isProcessing && (
                        <div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div className="bg-pink-600 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
                            </div>
                            <p className="text-sm text-center mt-2">{Math.round(progress)}% Complete</p>
                        </div>
                    )}
                    <div className="bg-gray-900 text-white font-mono text-xs rounded-lg p-4 h-64 overflow-y-auto">
                        {logs.map((log, i) => (
                            <p key={i} className={`${log.type === 'error' ? 'text-red-400' : log.type === 'warn' ? 'text-yellow-400' : 'text-green-400'}`}>
                                <span className="text-gray-500 mr-2">{log.time}</span>
                                {log.message}
                            </p>
                        ))}
                    </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <button onClick={onClose} disabled={isProcessing} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg disabled:opacity-50">Close</button>
                    <button onClick={processCsv} disabled={!file || isProcessing} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50">
                        {isProcessing ? 'Processing...' : 'Upload and Process'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Detailed Tracker Components ---

const PaperTrackerAdmin = ({ paper, onBack }) => {
    const [sections, setSections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newSectionName, setNewSectionName] = useState("");
    const [newTopic, setNewTopic] = useState({ sectionId: '', name: '' });
    const [newTask, setNewTask] = useState({ topicId: '', name: '', column: TASK_COLUMNS[0], link: '', grades: { A: '', B: '', C: '' } });
    const [examDate, setExamDate] = useState('');
    const [deletingSection, setDeletingSection] = useState(null); // {id, name}
    const [deletingTask, setDeletingTask] = useState(null); // {sectionId, topicId, taskId}

    // --- Timer State and Logic for Admin ---
    const [seconds, setSeconds] = useState(0);
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        let interval = null;
        if (isActive) {
            interval = setInterval(() => {
                setSeconds(s => s + 1);
            }, 1000);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isActive]);

    const toggleTimer = () => {
        setIsActive(!isActive);
    };

    const resetTimer = () => {
        setSeconds(0);
        setIsActive(false);
    };
    // --- End Timer State ---

    const fetchSectionsAndTopics = useCallback(async () => {
        setIsLoading(true);

        const paperDocRef = doc(db, 'papers', paper.id);
        const paperDocSnap = await getDoc(paperDocRef);
        if (paperDocSnap.exists()) {
            setExamDate(paperDocSnap.data().examDate || '');
        }

        const sectionsRef = collection(db, 'papers', paper.id, 'sections');
        const q = query(sectionsRef, orderBy("createdAt"));
        const sectionsSnapshot = await getDocs(q);

        const sectionsData = await Promise.all(sectionsSnapshot.docs.map(async (sectionDoc) => {
            const topicsRef = collection(db, 'papers', paper.id, 'sections', sectionDoc.id, 'topics');
            const topicsSnapshot = await getDocs(query(topicsRef, orderBy("createdAt")));

            const topicsData = await Promise.all(topicsSnapshot.docs.map(async (topicDoc) => {
               const tasksRef = collection(db, 'papers', paper.id, 'sections', sectionDoc.id, 'topics', topicDoc.id, 'tasks');
               const tasksSnapshot = await getDocs(query(tasksRef, orderBy("createdAt")));
               const tasksData = tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
               return { id: topicDoc.id, ...topicDoc.data(), tasks: tasksData };
            }));

            return { id: sectionDoc.id, ...sectionDoc.data(), topics: topicsData };
        }));

        setSections(sectionsData);
        setIsLoading(false);
    }, [paper.id]);

    useEffect(() => {
        fetchSectionsAndTopics();
    }, [fetchSectionsAndTopics]);

    const handleExamDateChange = async (e) => {
        const newDate = e.target.value;
        setExamDate(newDate);
        try {
            const paperRef = doc(db, 'papers', paper.id);
            await updateDoc(paperRef, { examDate: newDate });
        } catch (error) {
            console.error("Error updating exam date:", error);
        }
    };

    const handleAddSection = async (e) => {
        e.preventDefault();
        if (!newSectionName.trim()) return;
        await addDoc(collection(db, 'papers', paper.id, 'sections'), {
            name: newSectionName,
            createdAt: new Date()
        });
        setNewSectionName("");
        fetchSectionsAndTopics();
    };

    const confirmDeleteSection = async () => {
        if (!deletingSection) return;

        try {
            setIsLoading(true);
            const sectionId = deletingSection.id;
            const topicsRef = collection(db, 'papers', paper.id, 'sections', sectionId, 'topics');
            const topicsSnapshot = await getDocs(topicsRef);
            const batch = writeBatch(db);

            for (const topicDoc of topicsSnapshot.docs) {
                const tasksRef = collection(db, 'papers', paper.id, 'sections', sectionId, 'topics', topicDoc.id, 'tasks');
                const tasksSnapshot = await getDocs(tasksRef);
                tasksSnapshot.forEach(taskDoc => batch.delete(taskDoc.ref));
                batch.delete(topicDoc.ref);
            }

            const sectionRef = doc(db, 'papers', paper.id, 'sections', sectionId);
            batch.delete(sectionRef);

            await batch.commit();
            setDeletingSection(null);
            fetchSectionsAndTopics(); // This will set isLoading to false
        } catch (error) {
            console.error("Error deleting section:", error);
            setIsLoading(false);
            setDeletingSection(null);
        }
    };

    const handleAddTopic = async (e, sectionId) => {
        e.preventDefault();
        if (!newTopic.name.trim() || !sectionId) return;
        await addDoc(collection(db, 'papers', paper.id, 'sections', sectionId, 'topics'), {
            name: newTopic.name,
            createdAt: new Date(),
            probability: ""
        });
        setNewTopic({ sectionId: '', name: '' });
        fetchSectionsAndTopics();
    };


    const handleAddTask = async (e, sectionId, topicId) => {
        e.preventDefault();
        if (!newTask.name.trim() || !topicId) return;

        const taskData = {
            name: newTask.name,
            column: newTask.column,
            createdAt: new Date()
        };

        if (newTask.column === 'Class') {
            let url = newTask.link || "";
            if (url && !/^https?:\/\//i.test(url)) {
                url = 'https://' + url;
            }
            taskData.link = url;
        } else {
            taskData.grades = {
                A: newTask.grades.A || "",
                B: newTask.grades.B || "",
                C: newTask.grades.C || ""
            };
        }

        await addDoc(collection(db, 'papers', paper.id, 'sections', sectionId, 'topics', topicId, 'tasks'), taskData);
        setNewTask({ topicId: '', name: '', column: TASK_COLUMNS[0], questions: '', link: '', grades: { A: '', B: '', C: '' } });
        fetchSectionsAndTopics();
    };

    const confirmDeleteTask = async () => {
        if (!deletingTask) return;
        const { sectionId, topicId, taskId } = deletingTask;
        try {
            await deleteDoc(doc(db, 'papers', paper.id, 'sections', sectionId, 'topics', topicId, 'tasks', taskId));
            setDeletingTask(null);
            fetchSectionsAndTopics();
        } catch (error) {
            console.error("Error deleting task: ", error);
            setDeletingTask(null);
        }
    };

    const handleProbabilityChange = async (sectionId, topicId, value) => {
        setSections(prevSections => prevSections.map(s => {
            if (s.id === sectionId) {
                return {
                    ...s,
                    topics: s.topics.map(t => t.id === topicId ? { ...t, probability: value } : t)
                };
            }
            return s;
        }));

        const topicRef = doc(db, 'papers', paper.id, 'sections', sectionId, 'topics', topicId);
        await updateDoc(topicRef, { probability: value });
    };

    return (
        <div>
            <button onClick={onBack} className="mb-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">&larr; Back to Papers</button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <StudyTimer seconds={seconds} isActive={isActive} toggle={toggleTimer} reset={resetTimer} />
                <div className="bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Exam Countdown</h3>
                    <ExamCountdown examDate={examDate} />
                </div>
            </div>
             <div className="my-8">
                <StickyNotes paperId={paper.id} isAdmin={true} />
            </div>
            <Calendar
                paperId={paper.id}
                isAdmin={true}
                examDate={examDate}
                onExamDateChange={handleExamDateChange}
            />
            <div className="bg-white p-6 rounded-lg shadow-md mt-8">
                <h2 className="text-2xl font-bold mb-4">Manage Tracker for: <span className="text-pink-600">{paper.name}</span></h2>

                <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Topics and Checklist</h3>
                    <form onSubmit={handleAddSection} className="mb-6 flex gap-2">
                        <input
                            type="text"
                            value={newSectionName}
                            onChange={e => setNewSectionName(e.target.value)}
                            placeholder="New Section Name"
                            className="flex-grow px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        />
                        <button type="submit" className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded-lg">Add Section</button>
                    </form>

                    {isLoading ? <p>Loading tracker...</p> : (
                        <div className="space-y-8">
                            {sections.map(section => (
                                <div key={section.id} className="p-4 border-2 border-gray-200 rounded-lg">
                                    <div className="flex justify-between items-center mb-4 pb-4 border-b">
                                        <h4 className="text-2xl font-semibold text-gray-700">{section.name}</h4>
                                        <button onClick={() => setDeletingSection(section)} className="text-red-500 hover:text-red-700 text-sm">Delete Section</button>
                                    </div>

                                    {section.topics.map(topic => (
                                        <div key={topic.id} className="p-4 border rounded-lg mb-4">
                                            <h5 className="text-xl font-semibold">{topic.name}</h5>

                                            <table className="min-w-full mt-4">
                                                <thead>
                                                    <tr className="border-b">
                                                        {TASK_COLUMNS.map(col => <th key={col} className="pb-2 text-left font-semibold text-gray-600">{col}</th>)}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        {TASK_COLUMNS.map(col => (
                                                            <td key={col} className="align-top pt-2 pr-2">
                                                                <ul className="space-y-2">
                                                                    {topic.tasks.filter(t => t.column === col).map(task => (
                                                                        <li key={task.id} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                                                                            <div>
                                                                                <span className="font-bold">{task.name}</span>
                                                                                {task.link && <a href={task.link} target="_blank" rel="noopener noreferrer" className="text-xs text-pink-500 ml-2">(Link)</a>}
                                                                                {task.grades && Object.entries(task.grades).map(([grade, qs]) => qs && <div key={grade} className="text-xs text-gray-600 pl-2">{grade}: {qs}</div>)}
                                                                            </div>
                                                                            <button onClick={() => setDeletingTask({ sectionId: section.id, topicId: topic.id, taskId: task.id })} className="text-red-500 hover:text-red-700">
                                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                </svg>
                                                                            </button>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                </tbody>
                                            </table>

                                            <form onSubmit={(e) => handleAddTask(e, section.id, topic.id)} className="mt-4 border-t pt-4 space-y-2">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                    <input
                                                        type="text"
                                                        onChange={e => setNewTask({ ...newTask, name: e.target.value })}
                                                        placeholder="New Task Name"
                                                        className="md:col-span-2 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                                                    />
                                                    <select
                                                        value={newTask.column}
                                                        onChange={e => setNewTask({ ...newTask, column: e.target.value })}
                                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm"
                                                    >
                                                        {TASK_COLUMNS.map(col => <option key={col} value={col}>{col}</option>)}
                                                    </select>
                                                </div>
                                                {newTask.column === 'Class' ? (
                                                    <input
                                                        type="text"
                                                        onChange={e => setNewTask({ ...newTask, link: e.target.value })}
                                                        placeholder="www.example.com"
                                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                                                    />
                                                ) : (
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                                        <input type="text" onChange={e => setNewTask(t => ({...t, grades: {...t.grades, A: e.target.value}}))} placeholder="A: Q#s" className="px-3 py-2 bg-white border border-gray-300 rounded-md"/>
                                                        <input type="text" onChange={e => setNewTask(t => ({...t, grades: {...t.grades, B: e.target.value}}))} placeholder="B: Q#s" className="px-3 py-2 bg-white border border-gray-300 rounded-md"/>
                                                        <input type="text" onChange={e => setNewTask(t => ({...t, grades: {...t.grades, C: e.target.value}}))} placeholder="C: Q#s" className="px-3 py-2 bg-white border border-gray-300 rounded-md"/>
                                                    </div>
                                                )}
                                                <button type="submit" className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg">Add Task</button>
                                            </form>
                                        </div>
                                    ))}
                                    <form onSubmit={(e) => handleAddTopic(e, section.id)} className="mt-4 flex gap-2 border-t pt-4">
                                        <input
                                            type="text"
                                            onChange={e => setNewTopic({ ...newTopic, name: e.target.value })}
                                            placeholder="New Topic in this Section"
                                            className="flex-grow px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                                        />
                                        <button type="submit" className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg">Add Topic</button>
                                    </form>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Topics and Probability</h3>
                    {isLoading ? <p>Loading...</p> : (
                        <div className="space-y-4">
                            {sections.map(section => (
                                <div key={section.id} className="p-4 border-2 border-gray-200 rounded-lg">
                                    <h4 className="text-lg font-semibold text-gray-700 mb-2">{section.name}</h4>
                                    <ul className="space-y-2">
                                        {section.topics
                                            .slice()
                                            .sort((a, b) => {
                                                const valA = getSortValue(a.probability);
                                                const valB = getSortValue(b.probability);
                                                if (typeof valA === 'number' && typeof valB === 'number') {
                                                    return valA - valB;
                                                }
                                                return String(valA).localeCompare(String(valB));
                                            })
                                            .map(topic => (
                                            <li key={topic.id} className="flex items-center justify-between">
                                                <span className="text-gray-800">{topic.name}</span>
                                                <input
                                                    type="text"
                                                    placeholder="e.g., High, 75%"
                                                    value={topic.probability || ''}
                                                    onChange={(e) => handleProbabilityChange(section.id, topic.id, e.target.value)}
                                                    className="w-48 px-2 py-1 bg-white border border-gray-300 rounded-md shadow-sm"
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {deletingSection && (
                <ConfirmModal
                    title="Confirm Section Deletion"
                    message={`Are you sure you want to delete the section "${deletingSection.name}" and all its contents? This action cannot be undone.`}
                    onConfirm={confirmDeleteSection}
                    onCancel={() => setDeletingSection(null)}
                    confirmText="Delete"
                />
            )}
            {deletingTask && (
                <ConfirmModal
                    title="Confirm Task Deletion"
                    message="Are you sure you want to delete this task?"
                    onConfirm={confirmDeleteTask}
                    onCancel={() => setDeletingTask(null)}
                    confirmText="Delete"
                />
            )}
        </div>
    );
};

// --- Study Advisor Component ---
const StudyAdvisor = ({ sections, progress }) => {
    const getProbabilityWeight = (probability = '') => {
        const prob = String(probability).toLowerCase().trim();
        if (prob.endsWith('%')) {
            const num = parseFloat(prob);
            return isNaN(num) ? 10 : num;
        }
        if (prob === 'high') return 100;
        if (prob === 'medium') return 50;
        if (prob === 'low') return 25;
        return 10;
    };

    const calculateTopicCompletion = (topic) => {
        let totalTasks = 0;
        let completedTasks = 0;

        topic.tasks.forEach(task => {
            if (task.column === 'Class') {
                totalTasks++;
                if (progress[task.id]?.main?.checked) completedTasks++;
            } else {
                ['A', 'B', 'C'].forEach(grade => {
                    if (task.grades && task.grades[grade]) {
                        totalTasks++;
                        if (progress[task.id]?.[grade]?.checked) completedTasks++;
                    }
                });
            }
        });

        return totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    };

    const recommendations = sections
        .flatMap(section => section.topics)
        .map(topic => {
            const completion = calculateTopicCompletion(topic);
            const probabilityWeight = getProbabilityWeight(topic.probability);
            // Score prioritizes incomplete topics with high probability
            const score = (100.1 - completion) * probabilityWeight;
            return { ...topic, completion, score };
        })
        .filter(topic => topic.completion < 100)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center mb-4">
                <div>
                    <h3 className="text-xl font-bold text-gray-800">instilai</h3>
                    <p className="text-sm text-gray-500">Here's what you should focus on next!</p>
                </div>
            </div>
            {recommendations.length > 0 ? (
                <ul className="space-y-4">
                    {recommendations.map((rec, index) => (
                        <li key={rec.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                            <div className="flex justify-between items-center">
                                <span className="font-bold text-lg text-pink-700">#{index + 1} Priority</span>
                                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                    getProbabilityWeight(rec.probability) >= 90 ? 'bg-red-100 text-red-800' :
                                    getProbabilityWeight(rec.probability) >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                }`}>
                                    {rec.probability || 'N/A'}
                                </span>
                            </div>
                            <p className="font-semibold text-gray-900 mt-2">{rec.name}</p>
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                                <div className="bg-pink-600 h-2.5 rounded-full" style={{width: `${rec.completion}%`}}></div>
                            </div>
                            <p className="text-right text-xs text-gray-500 mt-1">{Math.round(rec.completion)}% complete</p>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-center py-8">
                    <p className="font-semibold text-green-600">Great job!</p>
                    <p className="text-gray-500">You've completed all available topics. Keep up the good work!</p>
                </div>
            )}
        </div>
    );
};


const UserPaperTracker = ({ paper, userId, onBack, timerProps }) => {
    const [sections, setSections] = useState([]);
    const [progress, setProgress] = useState({});
    const [calendarProgress, setCalendarProgress] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [viewingQuestions, setViewingQuestions] = useState(null);
    const [examDate, setExamDate] = useState(null);

    const fetchTrackerData = useCallback(async () => {
        setIsLoading(true);

        const paperDocRef = doc(db, 'papers', paper.id);
        const paperDocSnap = await getDoc(paperDocRef);
        if (paperDocSnap.exists()) {
            setExamDate(paperDocSnap.data().examDate);
        }

        const sectionsRef = collection(db, 'papers', paper.id, 'sections');
        const q = query(sectionsRef, orderBy("createdAt"));
        const sectionsSnapshot = await getDocs(q);

        const sectionsData = await Promise.all(sectionsSnapshot.docs.map(async (sectionDoc) => {
            const topicsRef = collection(db, 'papers', paper.id, 'sections', sectionDoc.id, 'topics');
            const topicsSnapshot = await getDocs(query(topicsRef, orderBy("createdAt")));

            const topicsData = await Promise.all(topicsSnapshot.docs.map(async (topicDoc) => {
               const tasksRef = collection(db, 'papers', paper.id, 'sections', sectionDoc.id, 'topics', topicDoc.id, 'tasks');
               const tasksSnapshot = await getDocs(query(tasksRef, orderBy("createdAt")));
               const tasksData = tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
               return { id: topicDoc.id, ...topicDoc.data(), tasks: tasksData };
            }));

            return { id: sectionDoc.id, ...sectionDoc.data(), topics: topicsData };
        }));
        setSections(sectionsData);

        const userDocRef = doc(db, 'users', userId);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setProgress(userData.accessiblePapers?.[paper.id]?.progress || {});
            setCalendarProgress(userData.accessiblePapers?.[paper.id]?.calendarProgress || {});
        }
        setIsLoading(false);
    }, [paper.id, userId]);

    useEffect(() => {
        const unsub = onSnapshot(doc(db, "users", userId), (doc) => {
             if (doc.exists()) {
                const userData = doc.data();
                setProgress(userData.accessiblePapers?.[paper.id]?.progress || {});
                setCalendarProgress(userData.accessiblePapers?.[paper.id]?.calendarProgress || {});
            }
        });
        fetchTrackerData();
        return () => unsub();
    }, [fetchTrackerData, userId, paper.id]);

    const handleProgressChange = async (taskId, grade, value) => {
        const newProgress = {
            ...progress,
            [taskId]: {
                ...progress[taskId],
                [grade]: { ...progress[taskId]?.[grade], checked: value }
            }
        };
        setProgress(newProgress);

        const userDocRef = doc(db, 'users', userId);
        const progressPath = `accessiblePapers.${paper.id}.progress.${taskId}.${grade}.checked`;
        await updateDoc(userDocRef, { [progressPath]: value });
    };

    const handleCalendarTaskProgressChange = async (calendarTask, provider, grade, value) => {
        const calendarTaskId = calendarTask.id;
        const newCalendarProgress = {
            ...calendarProgress,
            [calendarTaskId]: {
                ...calendarProgress[calendarTaskId],
                [provider]: {
                    ...calendarProgress[calendarTaskId]?.[provider],
                    [grade]: value
                }
            }
        };
        setCalendarProgress(newCalendarProgress);
    
        const userDocRef = doc(db, 'users', userId);
        const calendarProgressPath = `accessiblePapers.${paper.id}.calendarProgress.${calendarTaskId}.${provider}.${grade}`;
        
        // Find the corresponding main checklist task to update it as well
        const topicId = calendarTask.topicId;
        const column = provider.charAt(0).toUpperCase() + provider.slice(1);
        let checklistTaskId = null;
    
        for (const section of sections) {
            const topic = section.topics.find(t => t.id === topicId);
            if (topic) {
                const correspondingTask = topic.tasks.find(t => t.column === column && t.grades && t.grades[grade] === calendarTask[provider][grade]);
                if (correspondingTask) {
                    checklistTaskId = correspondingTask.id;
                    break;
                }
            }
        }
    
        const batch = writeBatch(db);
        batch.update(userDocRef, { [calendarProgressPath]: value });
    
        if (checklistTaskId) {
            const checklistProgressPath = `accessiblePapers.${paper.id}.progress.${checklistTaskId}.${grade}.checked`;
            batch.update(userDocRef, { [checklistProgressPath]: value });
        }
    
        await batch.commit();
    };

    const handleLearnClick = (task, grade) => {
        if (task.column === 'Class' && task.link) {
            let url = task.link;
            if (!/^https?:\/\//i.test(url)) {
                url = 'https://' + url;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        } else if (task.grades && task.grades[grade]) {
            setViewingQuestions({ name: `${task.name} - ${grade}`, questions: task.grades[grade] });
        }
    };

    const calculateProgress = useCallback(() => {
        let totalTasks = 0;
        let completedTasks = 0;

        sections.forEach(section => {
            section.topics.forEach(topic => {
                topic.tasks.forEach(task => {
                    if (task.column === 'Class') {
                        totalTasks++;
                        if (progress[task.id]?.main?.checked) {
                            completedTasks++;
                        }
                    } else {
                        ['A', 'B', 'C'].forEach(grade => {
                            if (task.grades && task.grades[grade]) {
                                totalTasks++;
                                if (progress[task.id]?.[grade]?.checked) {
                                    completedTasks++;
                                }
                            }
                        });
                    }
                });
            });
        });

        return totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    }, [sections, progress]);

    return (
        <>
            <button onClick={onBack} className="mb-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">&larr; Back to My Papers</button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <StudyTimer {...timerProps} />
                <div className="bg-white p-4 rounded-lg shadow-md flex flex-col items-center justify-center">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Exam Countdown</h3>
                    <ExamCountdown examDate={examDate} />
                </div>
            </div>
             <MotivationalQuotes />
             <div className="my-8">
                <StickyNotes paperId={paper.id} userId={userId} isAdmin={false} />
            </div>
            <Calendar
                paperId={paper.id}
                isAdmin={false}
                examDate={examDate}
                userId={userId}
                calendarProgress={calendarProgress}
                onCalendarTaskProgressChange={handleCalendarTaskProgressChange}
            />

            <div className="bg-white p-6 rounded-lg shadow-md mt-8">
                <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
                    <h2 className="text-2xl font-bold">My Progress for: <span className="text-pink-600">{paper.name}</span></h2>
                    <div className="flex flex-col md:flex-row items-center gap-6">
                        {!isLoading && <CircularProgressBar progress={calculateProgress()} />}
                    </div>
                </div>

                {isLoading ? <p>Loading tracker...</p> : (
                    <>
                        <div className="mb-8">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Topics and Checklist</h3>
                            <div className="space-y-8">
                                {sections.map(section => (
                                     <div key={section.id} className="p-4 border-2 border-gray-200 rounded-lg">
                                        <h4 className="text-2xl font-semibold text-gray-700 mb-4 pb-4 border-b">{section.name}</h4>
                                        <div className="space-y-6">
                                            {section.topics.map(topic => (
                                                <div key={topic.id} className="p-4 border rounded-lg">
                                                    <h5 className="text-xl font-semibold mb-4">{topic.name}</h5>
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full">
                                                            <thead className="border-b">
                                                                <tr>
                                                                    {TASK_COLUMNS.map(col => <th key={col} className="p-2 text-left font-semibold text-gray-600">{col}</th>)}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    {TASK_COLUMNS.map(col => (
                                                                        <td key={col} className="align-top p-2">
                                                                            <ul className="space-y-3">
                                                                                {topic.tasks.filter(t => t.column === col).map(task => (
                                                                                    <li key={task.id} className="text-sm">
                                                                                        <div className="font-bold mb-1">{task.name}</div>
                                                                                        {task.column === 'Class' ? (
                                                                                             <label htmlFor={`task-${task.id}`} className="flex items-center cursor-pointer">
                                                                                                <input type="checkbox" id={`task-${task.id}`} checked={progress[task.id]?.main?.checked || false} onChange={e => handleProgressChange(task.id, 'main', e.target.checked)} className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"/>
                                                                                                {task.link && <button onClick={() => handleLearnClick(task)} className="text-xs text-pink-600 hover:underline ml-2">Learn</button>}
                                                                                            </label>
                                                                                        ) : (
                                                                                            <ul className="pl-4 space-y-2">
                                                                                                {['A', 'B', 'C'].map(grade => task.grades?.[grade] && (
                                                                                                    <li key={grade} className="flex items-center justify-between">
                                                                                                        <label htmlFor={`task-${task.id}-${grade}`} className="flex items-center cursor-pointer">
                                                                                                            <input type="checkbox" id={`task-${task.id}-${grade}`} checked={progress[task.id]?.[grade]?.checked || false} onChange={e => handleProgressChange(task.id, grade, e.target.checked)} className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"/>
                                                                                                            <span className="ml-2">{grade}</span>
                                                                                                        </label>
                                                                                                        <button onClick={() => handleLearnClick(task, grade)} className="text-xs text-pink-600 hover:underline">Learn</button>
                                                                                                    </li>
                                                                                                ))}
                                                                                            </ul>
                                                                                        )}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-8">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Topics and Probability</h3>
                             <div className="space-y-4">
                                {sections.map(section => (
                                    <div key={section.id} className="p-4 border-2 border-gray-200 rounded-lg">
                                        <h4 className="text-lg font-semibold text-gray-700 mb-2">{section.name}</h4>
                                        <ul className="space-y-2">
                                            {section.topics
                                                .slice()
                                                .sort((a, b) => {
                                                    const valA = getSortValue(a.probability);
                                                    const valB = getSortValue(b.probability);
                                                    if (typeof valA === 'number' && typeof valB === 'number') {
                                                        return valA - valB;
                                                    }
                                                    return String(valA).localeCompare(String(valB));
                                                })
                                                .map(topic => (
                                                <li key={topic.id} className="flex items-center justify-between">
                                                    <span className="text-gray-800">{topic.name}</span>
                                                    <span className="font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded">{topic.probability || 'N/A'}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="mt-8">
                {!isLoading && <StudyAdvisor sections={sections} progress={progress} />}
            </div>

            {viewingQuestions && <QuestionPopup task={viewingQuestions} onClose={() => setViewingQuestions(null)} />}
        </>
    );
};

// --- Calendar Components ---
const Calendar = ({ paperId, isAdmin, examDate, onExamDateChange, userId, calendarProgress, onCalendarTaskProgressChange }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [tasks, setTasks] = useState({}); // Store tasks by date string 'YYYY-MM-DD'
    const [selectedDate, setSelectedDate] = useState(null);
    const [paperStructure, setPaperStructure] = useState([]);
    const [isLoadingStructure, setIsLoadingStructure] = useState(true);

    useEffect(() => {
        const fetchPaperStructure = async () => {
            if (!paperId) return;
            setIsLoadingStructure(true);
            const sectionsRef = collection(db, 'papers', paperId, 'sections');
            const q = query(sectionsRef, orderBy("createdAt"));
            const sectionsSnapshot = await getDocs(q);

            const sectionsData = await Promise.all(sectionsSnapshot.docs.map(async (sectionDoc) => {
                const topicsRef = collection(db, 'papers', paperId, 'sections', sectionDoc.id, 'topics');
                const topicsSnapshot = await getDocs(query(topicsRef, orderBy("createdAt")));

                const topicsData = await Promise.all(topicsSnapshot.docs.map(async (topicDoc) => {
                   const tasksRef = collection(db, 'papers', paperId, 'sections', sectionDoc.id, 'topics', topicDoc.id, 'tasks');
                   const tasksSnapshot = await getDocs(query(tasksRef, orderBy("createdAt")));
                   const tasksData = tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                   return { id: topicDoc.id, ...topicDoc.data(), tasks: tasksData };
                }));

                return { id: sectionDoc.id, ...sectionDoc.data(), topics: topicsData };
            }));
            setPaperStructure(sectionsData);
            setIsLoadingStructure(false);
        };

        fetchPaperStructure();
    }, [paperId]);

    useEffect(() => {
        if (!paperId) return;

        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const startOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month + 1, 0).getDate();
        const endOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

        const tasksRef = collection(db, 'papers', paperId, 'calendarTasks');
        const q = query(tasksRef, where('date', '>=', startOfMonth), where('date', '<=', endOfMonth));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newTasks = {};
            snapshot.forEach(doc => {
                const data = doc.data();
                const date = data.date;
                if (!newTasks[date]) {
                    newTasks[date] = [];
                }
                newTasks[date].push({ id: doc.id, ...data });
            });
            setTasks(newTasks);
        });

        return () => unsubscribe();
    }, [paperId, currentDate]);

    const handleDateClick = (day) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        setSelectedDate(date);
    };

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthName = currentDate.toLocaleString('default', { month: 'long' });

    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const blanks = Array(firstDayOfMonth).fill(null);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const today = new Date();

    const selectedDateString = selectedDate
        ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
        : null;

    const dailyTasks = selectedDateString ? tasks[selectedDateString] || [] : [];

    return (
        <div className="bg-white p-4 rounded-lg shadow-md mb-8">
            {isAdmin && (
                <div className="mb-4 bg-gray-50 p-3 rounded-lg border">
                    <label htmlFor="examDate" className="block text-sm font-medium text-gray-700 mb-1">Set Exam Date</label>
                    <input
                        type="date"
                        id="examDate"
                        value={examDate || ''}
                        onChange={onExamDateChange}
                        className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                </div>
            )}
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="p-2 rounded-full hover:bg-gray-200">
                    &lt;
                </button>
                <h3 className="text-lg font-semibold">{monthName} {year}</h3>
                <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} className="p-2 rounded-full hover:bg-gray-200">
                    &gt;
                </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-sm font-semibold text-gray-600 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day}>{day}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {blanks.map((_, i) => <div key={`blank-${i}`} className="border rounded-lg"></div>)}
                {days.map(day => {
                    const monthString = String(month + 1).padStart(2, '0');
                    const dayString = String(day).padStart(2, '0');
                    const dateString = `${year}-${monthString}-${dayString}`;

                    const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
                    const hasTasks = (tasks[dateString] || []).length > 0;
                    const isSelected = selectedDateString === dateString;
                    const isExamDay = examDate && dateString === examDate;

                    return (
                        <div
                            key={day}
                            className={`border rounded-lg p-1 min-h-[60px] flex flex-col cursor-pointer transition-colors ${
                                isExamDay ? 'bg-purple-600 text-white hover:bg-purple-700' :
                                isSelected ? 'bg-pink-100 border-pink-400' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => handleDateClick(day)}
                        >
                            <span className={`font-bold self-start w-6 h-6 flex items-center justify-center rounded-full ${
                                isToday && !isExamDay ? 'bg-pink-600 text-white' : ''
                            } ${
                                isToday && isExamDay ? 'ring-2 ring-white' : ''
                            }`}>
                                {day}
                            </span>
                            {hasTasks && !isExamDay && <div className="w-2 h-2 bg-green-500 rounded-full self-center mt-1"></div>}
                        </div>
                    );
                })}
            </div>
            <DailyTaskManager
                paperId={paperId}
                isAdmin={isAdmin}
                selectedDate={selectedDate}
                dailyTasks={dailyTasks}
                paperStructure={paperStructure}
                isLoadingStructure={isLoadingStructure}
                userId={userId}
                calendarProgress={calendarProgress}
                onCalendarTaskProgressChange={onCalendarTaskProgressChange}
            />
        </div>
    );
};

const DailyTaskManager = ({ paperId, isAdmin, selectedDate, dailyTasks, paperStructure, isLoadingStructure, userId, calendarProgress, onCalendarTaskProgressChange }) => {
    const [deletingTask, setDeletingTask] = useState(null); // For confirming deletion of the whole task

    const confirmDeleteTask = async () => {
        if (!deletingTask) return;
        try {
            await deleteDoc(doc(db, 'papers', paperId, 'calendarTasks', deletingTask.id));
            setDeletingTask(null);
        } catch (error) {
            console.error("Error deleting entire calendar task:", error);
            setDeletingTask(null);
        }
    };

    const handleDeleteSubTask = async (task, provider, grade) => {
        const providerKey = provider.toLowerCase();
        const fieldToDelete = `${providerKey}.${grade}`;
        const taskRef = doc(db, 'papers', paperId, 'calendarTasks', task.id);

        try {
            await updateDoc(taskRef, {
                [fieldToDelete]: deleteField()
            });

            const updatedTaskDoc = await getDoc(taskRef);
            if (updatedTaskDoc.exists()) {
                const data = updatedTaskDoc.data();

                const hasRemainingQuestions = CALENDAR_PROVIDERS.some(p => {
                    const key = p.toLowerCase();
                    return data[key] && Object.keys(data[key]).length > 0;
                });

                if (!hasRemainingQuestions) {
                    await deleteDoc(taskRef);
                }
            }
        } catch (error) {
            console.error("Error updating/deleting calendar task:", error);
        }
    };

    if (!selectedDate) {
        return <div className="text-center text-gray-500 pt-8">Select a date to view or add tasks.</div>;
    }

    return (
        <div className="mt-6">
            <h4 className="text-lg font-bold mb-4">Tasks for {selectedDate.toLocaleDateString()}</h4>
            <div className="space-y-4">
                {dailyTasks.map(task => (
                    <div key={task.id} className="bg-gray-50 p-4 rounded-lg shadow">
                        <div className="flex justify-between items-start mb-2">
                            <h5 className="font-bold text-gray-800">{task.topic}</h5>
                            {isAdmin && (
                                <button
                                    onClick={() => setDeletingTask(task)}
                                    className="text-sm text-red-600 hover:underline"
                                >
                                    Delete Task
                                </button>
                            )}
                        </div>
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                                <tr>
                                    <th className="px-4 py-2">Provider</th>
                                    <th className="px-4 py-2">A</th>
                                    <th className="px-4 py-2">B</th>
                                    <th className="px-4 py-2">C</th>
                                </tr>
                            </thead>
                            <tbody>
                                {CALENDAR_PROVIDERS.map(provider => (
                                    <tr key={provider} className="border-b">
                                        <td className="px-4 py-2 font-medium">{provider}</td>
                                        {['A', 'B', 'C'].map(grade => {
                                            const providerKey = provider.toLowerCase();
                                            const question = task[providerKey]?.[grade];
                                            const isChecked = calendarProgress?.[task.id]?.[providerKey]?.[grade] || false;

                                            return (
                                                <td key={grade} className="px-4 py-2">
                                                    <div className="flex justify-between items-center">
                                                        {!isAdmin && question ? (
                                                            <label className="flex items-center space-x-2 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={(e) => onCalendarTaskProgressChange(task, providerKey, grade, e.target.checked)}
                                                                    className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                                                                />
                                                                <span>{question}</span>
                                                            </label>
                                                        ) : (
                                                            <span>{question || '-'}</span>
                                                        )}
                                                        {isAdmin && question && (
                                                            <button
                                                                onClick={() => handleDeleteSubTask(task, provider, grade)}
                                                                className="text-red-500 hover:text-red-700 ml-2 p-1 rounded-full hover:bg-red-100"
                                                                title={`Delete ${provider} ${grade}`}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
            {dailyTasks.length === 0 && <p className="text-gray-500">No tasks for this day.</p>}

            {isAdmin && (
                <div className="mt-6 pt-6 border-t">
                    <h5 className="text-lg font-bold mb-4">Add New Task</h5>
                    {isLoadingStructure ? <p>Loading topics...</p> : (
                        <CalendarTaskForm
                            paperId={paperId}
                            selectedDate={selectedDate}
                            paperStructure={paperStructure}
                        />
                    )}
                </div>
            )}
            {deletingTask && (
                 <ConfirmModal
                    title="Confirm Task Deletion"
                    message={`Are you sure you want to delete the entire task for "${deletingTask.topic}" on this day? This cannot be undone.`}
                    onConfirm={confirmDeleteTask}
                    onCancel={() => setDeletingTask(null)}
                    confirmText="Delete"
                />
            )}
        </div>
    );
};

const CalendarTaskForm = ({ paperId, selectedDate, paperStructure }) => {
    const [formData, setFormData] = useState({
        topicId: '',
        topic: '',
        availableQuestions: { kaplan: {}, bpp: {}, studyhub: {} },
        selectedQuestions: { kaplan: {}, bpp: {}, studyhub: {} },
    });
    const [isLoading, setIsLoading] = useState(false);

    const allTopics = paperStructure.flatMap(section => section.topics);

    const handleTopicChange = (e) => {
        const topicId = e.target.value;
        const topic = allTopics.find(t => t.id === topicId);
        if (!topic) {
            setFormData({ topicId: '', topic: '', availableQuestions: { kaplan: {}, bpp: {}, studyhub: {} }, selectedQuestions: { kaplan: {}, bpp: {}, studyhub: {} } });
            return;
        }

        const available = { kaplan: {}, bpp: {}, studyhub: {} };
        topic.tasks.forEach(task => {
            const provider = task.column.toLowerCase();
            if (available[provider] && task.grades) {
                // FIX: Iterate through grades and only add them if they have a value,
                // preventing empty strings from overwriting existing values.
                for (const grade in task.grades) {
                    if (Object.prototype.hasOwnProperty.call(task.grades, grade) && task.grades[grade]) {
                        available[provider][grade] = task.grades[grade];
                    }
                }
            }
        });

        setFormData({
            topicId: topic.id,
            topic: topic.name,
            availableQuestions: available,
            selectedQuestions: { kaplan: {}, bpp: {}, studyhub: {} },
        });
    };

    const handleQuestionSelectionChange = (provider, grade, questionText) => {
        const newSelected = { ...formData.selectedQuestions };
        const providerKey = provider.toLowerCase();

        if (!newSelected[providerKey]) {
            newSelected[providerKey] = {};
        }

        if (newSelected[providerKey][grade]) {
            delete newSelected[providerKey][grade];
        } else {
            newSelected[providerKey][grade] = questionText;
        }

        setFormData(prev => ({ ...prev, selectedQuestions: newSelected }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.topicId) {
            // Replaced alert with console log for non-interactive environments
            console.error("Please select a topic.");
            return;
        }
        setIsLoading(true);

        const dateString = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

        const taskData = {
            date: dateString,
            topicId: formData.topicId,
            topic: formData.topic,
            kaplan: formData.selectedQuestions.kaplan || {},
            bpp: formData.selectedQuestions.bpp || {},
            studyhub: formData.selectedQuestions.studyhub || {},
            createdAt: new Date()
        };

        try {
            await addDoc(collection(db, 'papers', paperId, 'calendarTasks'), taskData);
            setFormData({
                topicId: '',
                topic: '',
                availableQuestions: { kaplan: {}, bpp: {}, studyhub: {} },
                selectedQuestions: { kaplan: {}, bpp: {}, studyhub: {} },
            });
        } catch (error) {
            console.error("Error saving calendar task:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="bg-gray-50 p-4 rounded-lg space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Topic</label>
                <select
                    value={formData.topicId}
                    onChange={handleTopicChange}
                    required
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                >
                    <option value="">-- Select a Topic --</option>
                    {allTopics.map(topic => (
                        <option key={topic.id} value={topic.id}>{topic.name}</option>
                    ))}
                </select>
            </div>

            {formData.topicId && (
                <div>
                    <h6 className="text-sm font-medium text-gray-700 mb-2">Select Questions to Assign:</h6>
                    <div className="space-y-3">
                        {CALENDAR_PROVIDERS.map(provider => {
                            const providerKey = provider.toLowerCase();
                            const questions = formData.availableQuestions[providerKey];
                            if (!questions || Object.keys(questions).every(k => !questions[k])) {
                                return null;
                            }
                            return (
                                <div key={providerKey}>
                                    <p className="font-semibold text-gray-800">{provider}</p>
                                    <div className="pl-4 mt-1 space-y-1">
                                        {['A', 'B', 'C'].map(grade => questions[grade] && (
                                            <label key={grade} className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={!!formData.selectedQuestions[providerKey]?.[grade]}
                                                    onChange={() => handleQuestionSelectionChange(provider, grade, questions[grade])}
                                                    className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                                                />
                                                <span className="text-sm text-gray-700"><b>{grade}:</b> {questions[grade]}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
                <button type="submit" disabled={isLoading || !formData.topicId} className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-pink-300 disabled:cursor-not-allowed">
                    {isLoading ? 'Adding...' : 'Add Task'}
                </button>
            </div>
        </form>
    );
};


const QuestionPopup = ({ task, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{task.name}</h3>
                    <p className="text-sm text-gray-500 mb-4">Associated Question Numbers:</p>
                    <div className="bg-gray-100 p-4 rounded-lg">
                        <p className="text-lg font-mono text-gray-800">{task.questions}</p>
                    </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                    <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Close</button>
                </div>
            </div>
        </div>
    );
};

// --- Sticky Notes Component ---
const StickyNotes = ({ paperId, userId, isAdmin }) => {
    const [homeworkNotes, setHomeworkNotes] = useState([]);
    const [studentNotes, setStudentNotes] = useState([]);
    const [newHomework, setNewHomework] = useState('');
    const [newStudentNote, setNewStudentNote] = useState('');

    // Fetch homework notes (for both admin and student)
    useEffect(() => {
        const paperRef = doc(db, 'papers', paperId);
        const unsubscribe = onSnapshot(paperRef, (doc) => {
            if (doc.exists()) {
                setHomeworkNotes(doc.data().homework || []);
            }
        });
        return () => unsubscribe();
    }, [paperId]);

    // Fetch student notes (only for students)
    useEffect(() => {
        if (isAdmin) return;
        const userRef = doc(db, 'users', userId);
        const unsubscribe = onSnapshot(userRef, (doc) => {
            if (doc.exists()) {
                const paperData = doc.data().accessiblePapers?.[paperId];
                setStudentNotes(paperData?.studentNotes || []);
            }
        });
        return () => unsubscribe();
    }, [paperId, userId, isAdmin]);

    const handleAddHomework = async (e) => {
        e.preventDefault();
        if (!newHomework.trim()) return;
        const paperRef = doc(db, 'papers', paperId);
        const newNote = { id: Date.now().toString(), text: newHomework };
        await updateDoc(paperRef, {
            homework: arrayUnion(newNote)
        });
        setNewHomework('');
    };

    const handleDeleteHomework = async (note) => {
        const paperRef = doc(db, 'papers', paperId);
        await updateDoc(paperRef, {
            homework: arrayRemove(note)
        });
    };

    const handleAddStudentNote = async (e) => {
        e.preventDefault();
        if (!newStudentNote.trim()) return;
        const userRef = doc(db, 'users', userId);
        const notePath = `accessiblePapers.${paperId}.studentNotes`;
        const newNote = { id: Date.now().toString(), text: newStudentNote };
        await updateDoc(userRef, {
            [notePath]: arrayUnion(newNote)
        });
        setNewStudentNote('');
    };
    
    const handleDeleteStudentNote = async (note) => {
        const userRef = doc(db, 'users', userId);
        const notePath = `accessiblePapers.${paperId}.studentNotes`;
        await updateDoc(userRef, {
            [notePath]: arrayRemove(note)
        });
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Homework Section */}
            <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">Homework</h3>
                {isAdmin && (
                    <form onSubmit={handleAddHomework} className="mb-4">
                        <textarea
                            value={newHomework}
                            onChange={(e) => setNewHomework(e.target.value)}
                            placeholder="Add new homework..."
                            className="w-full p-2 border rounded-lg focus:ring-pink-500 focus:border-pink-500"
                            rows="3"
                        ></textarea>
                        <button type="submit" className="mt-2 w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg">Add Homework</button>
                    </form>
                )}
                <div className="space-y-4">
                    {homeworkNotes.length > 0 ? homeworkNotes.map(note => (
                        <div key={note.id} className="bg-blue-100 p-4 rounded-lg shadow-md relative">
                            <p className="whitespace-pre-wrap">{note.text}</p>
                            {isAdmin && (
                                <button onClick={() => handleDeleteHomework(note)} className="absolute top-2 right-2 text-red-500 hover:text-red-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    )) : <p className="text-gray-500">No homework assigned yet.</p>}
                </div>
            </div>

            {/* Student Notes Section */}
            {!isAdmin && (
                <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">My Notes</h3>
                    <form onSubmit={handleAddStudentNote} className="mb-4">
                        <textarea
                            value={newStudentNote}
                            onChange={(e) => setNewStudentNote(e.target.value)}
                            placeholder="Add a personal note..."
                            className="w-full p-2 border rounded-lg focus:ring-pink-500 focus:border-pink-500"
                            rows="3"
                        ></textarea>
                        <button type="submit" className="mt-2 w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded-lg">Add Note</button>
                    </form>
                    <div className="space-y-4">
                        {studentNotes.length > 0 ? studentNotes.map(note => (
                            <div key={note.id} className="bg-yellow-200 p-4 rounded-lg shadow-md relative transform -rotate-1">
                                <p className="whitespace-pre-wrap">{note.text}</p>
                                <button onClick={() => handleDeleteStudentNote(note)} className="absolute top-2 right-2 text-gray-600 hover:text-gray-800">
                                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        )) : <p className="text-gray-500">You haven't added any notes yet.</p>}
                    </div>
                </div>
            )}
        </div>
    );
};
